import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBookingRouteId, getUserFacingBookingId } from '../utils/userFacingBookingId';
import { LifeBuoy, X, Loader2, CheckCircle2, Paperclip } from 'lucide-react';
import PhoneInput from './ui/PhoneInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTACH = 5;
const MAX_BYTES = 5 * 1024 * 1024;
const ALLOW_EXT = ['.png', '.jpg', '.jpeg', '.pdf', '.webp'];

export const SUPPORT_CATEGORIES = [
  { id: 'cancel_refund', label: 'Cancel / Refund' },
  { id: 'date_change', label: 'Date Change / Reschedule' },
  { id: 'name_correction', label: 'Name Correction' },
  { id: 'seat_selection', label: 'Seat Selection' },
  { id: 'add_baggage', label: 'Add Baggage' },
  { id: 'add_meal', label: 'Add Meal' },
  { id: 'special_request', label: 'Special Request' },
  { id: 'gst_invoice', label: 'GST / Invoice Issue' },
  { id: 'payment_wallet', label: 'Payment / Wallet Issue' },
  { id: 'schedule_change', label: 'Schedule Change' },
  { id: 'failed_booking', label: 'Failed Booking' },
  { id: 'reprice', label: 'Reprice / Fare Change' },
  { id: 'supplier_escalation', label: 'Supplier Escalation' },
  { id: 'group_booking', label: 'Group Booking' },
  { id: 'seat_block', label: 'Seat Block / Charter' },
  { id: 'general_inquiry', label: 'General Inquiry' },
  { id: 'other', label: 'Other' },
];

/**
 * Modal that lets a logged-in B2C user or agent raise a support ticket against
 * a specific booking. Opens from the "Need Help?" button on any booking card.
 *
 * Props:
 *   open              — bool
 *   onClose           — fn()
 *   booking           — the booking object (at minimum: id/booking_id)
 *   defaultEmail      — pre-fill email from caller profile
 *   defaultPhone      — pre-fill phone from caller profile
 *   onCreated         — callback(ticket) fired on success
 *   portal            — 'agent' | 'b2c' (default b2c) — affects success actions and styling
 *   defaultCategory   — initial category id when modal opens
 */
const RaiseTicketModal = ({
  open,
  onClose,
  booking,
  defaultEmail = '',
  defaultPhone = '',
  onCreated,
  portal = 'b2c',
  defaultCategory = 'cancel_refund',
}) => {
  const navigate = useNavigate();
  const isAgent = portal === 'agent';
  const [category, setCategory] = useState(defaultCategory);
  const [priority, setPriority] = useState('normal');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(defaultEmail);
  const [phone, setPhone] = useState(defaultPhone);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState(null);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (open) setCategory(defaultCategory);
  }, [open, defaultCategory]);

  if (!open) return null;

  const reset = () => {
    setCategory(defaultCategory); setPriority('normal'); setSubject(''); setMessage('');
    setErr(''); setSuccess(null); setSubmitting(false); setFiles([]);
  };

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;
    const next = [...files, ...picked].slice(0, MAX_ATTACH);
    setFiles(next);
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!message.trim()) { setErr('Please describe your issue'); return; }
    for (const f of files) {
      if (f.size > MAX_BYTES) {
        setErr(`${f.name} exceeds 5 MB`);
        return;
      }
      const ext = (f.name || '').slice(f.name.lastIndexOf('.')).toLowerCase();
      if (!ALLOW_EXT.includes(ext)) {
        setErr(`${f.name}: only PNG, JPG, JPEG, PDF, WEBP allowed`);
        return;
      }
    }
    setErr(''); setSubmitting(true);
    try {
      const bookingId = getApiBookingRouteId(booking) || booking.id || booking.booking_id;
      const r = await axios.post(`${API}/support/tickets`, {
        booking_id: bookingId,
        category, priority, subject, message,
        email: email || undefined,
        phone: phone || undefined,
      }, { withCredentials: true });
      const ticket = r.data.ticket;
      if (files.length > 0) {
        const form = new FormData();
        files.forEach((f) => form.append('files', f));
        await axios.post(`${API}/support/tickets/${ticket.id}/attach`, form, { withCredentials: true });
      }
      setSuccess(ticket);
      if (onCreated) onCreated(ticket);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to raise ticket');
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           data-testid="raise-ticket-modal"
           className="bg-white rounded-2xl max-w-xl w-full max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <LifeBuoy className={`w-5 h-5 ${isAgent ? 'text-teal-600' : 'text-orange-500'}`} />
            <h3 className="font-semibold text-gray-900">{success ? 'Ticket Raised' : 'Need Help?'}</h3>
          </div>
          <button onClick={() => { reset(); onClose(); }} className="p-1 text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>

        {success ? (
          <div className="p-8 text-center">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
            <h4 className="font-bold text-lg text-gray-900 mb-1">Your request has been submitted</h4>
            <p className="text-sm text-gray-600 mb-4">
              {isAgent
                ? 'ACF support will review your request. You can continue the conversation in My Support.'
                : 'Our team will get back to you shortly. You can track the conversation from your dashboard.'}
            </p>
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <div className="text-xs text-gray-500">Ticket Reference</div>
              <div className={`font-mono font-bold ${isAgent ? 'text-teal-700' : 'text-blue-600'}`}>{success.reference}</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              {isAgent && success.id && (
                <button
                  type="button"
                  data-testid="view-ticket-success-btn"
                  onClick={() => {
                    reset();
                    onClose();
                    navigate(`/agent/support-tickets/${success.id}`);
                  }}
                  className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white px-6 py-2 rounded-lg font-medium"
                >
                  View ticket
                </button>
              )}
              <button
                type="button"
                data-testid="close-success-btn"
                onClick={() => { reset(); onClose(); }}
                className={`px-6 py-2 rounded-lg font-medium ${
                  isAgent
                    ? 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    : 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                }`}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <div className="bg-blue-50 text-blue-800 text-xs p-2 rounded">
              Booking <strong>{getUserFacingBookingId(booking)}</strong>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">What do you need help with?</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                      data-testid="category-select"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {SUPPORT_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
              <div className="flex gap-2">
                {['normal', 'urgent', 'critical'].map((p) => (
                  <button key={p} onClick={() => setPriority(p)}
                          data-testid={`priority-${p}`}
                          className={`flex-1 text-xs py-2 rounded-lg border ${
                            priority === p
                              ? 'border-orange-500 bg-orange-50 text-orange-700 font-medium'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Subject (optional)</label>
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                     data-testid="subject-input"
                     placeholder="Brief summary"
                     className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Describe your issue *</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                        data-testid="message-input"
                        placeholder="Share all the details so we can help faster…"
                        rows={4}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Attachments (optional, max {MAX_ATTACH}, 5 MB each)</label>
              <label className="flex items-center gap-2 text-sm text-blue-700 cursor-pointer border border-dashed border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50">
                <Paperclip className="w-4 h-4" />
                <span>Choose files</span>
                <input type="file" multiple accept=".png,.jpg,.jpeg,.pdf,.webp" className="hidden" onChange={onPickFiles} data-testid="raise-ticket-files" />
              </label>
              {files.length > 0 && (
                <ul className="mt-1.5 text-xs text-gray-600 space-y-1">
                  {files.map((f, i) => (
                    <li key={`${f.name}-${i}`} className="flex justify-between gap-2">
                      <span className="truncate">{f.name}</span>
                      <button type="button" className="text-red-600 shrink-0" onClick={() => removeFile(i)}>Remove</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                       data-testid="email-input"
                       className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                <PhoneInput value={phone} onChange={setPhone} defaultCountry="IN" testid="phone-input" />
              </div>
            </div>

            {err && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{err}</div>}

            <div className="pt-2 flex gap-2">
              <button onClick={() => { reset(); onClose(); }}
                      className="flex-1 border border-gray-300 rounded-lg py-2 text-sm">Cancel</button>
              <button onClick={submit} disabled={submitting || !message.trim()}
                      data-testid="submit-ticket-btn"
                      className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Submit Request
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RaiseTicketModal;
